import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ElementOrientationService } from '@service/canvas/element-orientation.service';

export type OrientationPopupType = 'valve' | 'pump' | 'connector';

export interface OrientationPopupConfig {
    type: OrientationPopupType;
    element: SVGGElement;
    connectorType?: string;
}

interface IconOption {
    svgIcon: string;
    value: string;
    title?: string;
}

const CONNECTOR_ORIENTATIONS: Record<string, string[]> = {
    straight: ['lines-horizontal', 'lines-vertical'],
    corner: ['corner-corner-right-down', 'corner-corner-right-up', 'corner-corner-left-down', 'corner-corner-left-up'],
    direction: ['direction-right', 'direction-left', 'direction-down', 'direction-direction-3'],
    endpoint: ['end-point-right', 'end-point-left', 'end-point-up', 'end-point-down'],
    junction: [
        'cross-or-3-way-3-way-right',
        'cross-or-3-way-3-way-left',
        'cross-or-3-way-3-way-up',
        'cross-or-3-way-3-way-down',
        'cross-or-3-way-cross',
    ],
};

const PUMP_ICONS = ['pump-vertical-right', 'pump-vertical-left', 'pump-horizontal-right', 'pump-horizontal-left'];

const VALVE_ICONS_2WAY: IconOption[] = [
    {
        svgIcon: 'valve-hori-static-2-way-static-horizontal-no-indicator',
        value: 'horizontal',
        title: 'Horizontal',
    },
    {
        svgIcon: 'valve-vert-static-2-way-static-vertical-no-indicator',
        value: 'vertical',
        title: 'Vertical',
    },
];

const VALVE_ICONS_3WAY: IconOption[] = [
    {
        svgIcon: 'valve-3-hori-open-3-way-open-horizontal-no-indicator',
        value: 'horizontal',
        title: 'Horizontal',
    },
    {
        svgIcon: 'valve-3-vert-open-3-way-open-vertical-no-indicator',
        value: 'vertical',
        title: 'Vertical',
    },
];

@Component({
    selector: 'app-orientation-popup',
    templateUrl: './orientation-popup.component.html',
    styleUrls: ['./orientation-popup.component.css'],
    standalone: false,
})
export class OrientationPopupComponent implements OnChanges, OnDestroy {
    @Input() config: OrientationPopupConfig | null = null;

    x: number = 0;
    y: number = 0;
    icons: IconOption[] = [];

    private positionSubscription: Subscription | null = null;

    constructor(
        private orientationService: ElementOrientationService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['config']) {
            if (this.config) {
                this.updateIcons();
                // Reset position before starting tracking to ensure fresh position is calculated
                const initialPos = this.orientationService.getPopupPosition(this.config.element);
                if (initialPos) {
                    this.x = initialPos.x;
                    this.y = initialPos.y;
                }
                this.startPositionTracking();
            } else {
                this.stopPositionTracking();
                this.icons = [];
                this.x = 0;
                this.y = 0;
            }
        }
    }

    ngOnDestroy(): void {
        this.stopPositionTracking();
    }

    select(value: string, event: MouseEvent): void {
        event.stopPropagation();
        if (!this.config) return;

        switch (this.config.type) {
            case 'valve':
                this.handleValveOrientation(value as 'horizontal' | 'vertical');
                break;
            case 'pump':
                this.orientationService.applyPumpOrientation(this.config.element, value).pipe(take(1)).subscribe();
                break;
            case 'connector':
                this.orientationService.applyConnectorOrientation(this.config.element, value).pipe(take(1)).subscribe();
                break;
        }
    }

    private handleValveOrientation(orientation: 'horizontal' | 'vertical'): void {
        if (!this.config) return;

        const way = this.config.element.getAttribute('data-icreate-valve-way');
        const state = (this.config.element.getAttribute('data-icreate-valve-state') as 'static' | 'closed') || 'static';

        if (way === '3way') {
            const orientationSuffix = orientation === 'horizontal' ? 'hori' : 'vert';
            const iconName = `valve-3-${orientationSuffix}-open-3-way-open-${orientation}-no-indicator`;
            const existingStroke = this.config.element.querySelector('path')?.getAttribute('stroke');
            const hasBorder = !!existingStroke && existingStroke !== 'none';

            this.orientationService
                .applyValveIcon3Way(this.config.element, iconName, orientation, state)
                .pipe(take(1))
                .subscribe(() => {
                    if (!hasBorder || !this.config) return;
                    this.config.element.querySelectorAll('path').forEach(path => {
                        path.setAttribute('stroke', '#2c3a48');
                        path.setAttribute('stroke-width', '1.5');
                    });
                });
        } else {
            this.orientationService.applyValveOrientation(this.config.element, orientation, state);
        }
    }

    private updateIcons(): void {
        if (!this.config) return;

        switch (this.config.type) {
            case 'valve':
                const way = this.config.element.getAttribute('data-icreate-valve-way');
                this.icons = way === '3way' ? VALVE_ICONS_3WAY : VALVE_ICONS_2WAY;
                break;
            case 'pump':
                this.icons = PUMP_ICONS.map(name => ({
                    svgIcon: name,
                    value: name,
                }));
                break;
            case 'connector':
                const connectorIcons = CONNECTOR_ORIENTATIONS[this.config.connectorType ?? ''] ?? [];
                this.icons = connectorIcons.map(name => ({
                    svgIcon: name,
                    value: name,
                }));
                break;
        }
    }

    private startPositionTracking(): void {
        this.stopPositionTracking();
        if (!this.config) return;

        this.positionSubscription = this.orientationService.watchElementPosition(this.config.element).subscribe(pos => {
            this.x = pos.x;
            this.y = pos.y;
            this.cdr.markForCheck();
        });
    }

    private stopPositionTracking(): void {
        this.positionSubscription?.unsubscribe();
        this.positionSubscription = null;
    }

    getIconStyleVariant(): string {
        return this.config ? this.config.type : '';
    }
}
